import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { Check, ArrowRight, BarChart3, TrendingUp, Users, Mail, Calendar, Eye } from 'lucide-react'
import { APP_URL } from '@/lib/public-trpc'
import { capability } from '@gjl/voice/pricing'

const websiteCapability = capability('website')!

const features = [
  {
    icon: Eye,
    title: 'Website Analytics',
    description:
      'Track page views, visitor counts, and traffic sources. See which pages perform best and where your audience comes from.',
  },
  {
    icon: Mail,
    title: 'Email Performance',
    description: 'Track opens, clicks, and engagement rates. Know which subject lines work and what content resonates.',
  },
  {
    icon: Calendar,
    title: 'Event Metrics',
    description:
      'Track RSVPs, attendance, and no-show rates. See which events are most popular and plan your season accordingly.',
  },
  {
    icon: Users,
    title: 'Audience Growth',
    description:
      'Watch your subscriber list grow over time. Track where new subscribers come from and your retention rate.',
  },
  {
    icon: TrendingUp,
    title: 'Revenue Tracking',
    description:
      'Track donation totals, ticket sales, and revenue trends. Understand your financial health at a glance.',
  },
  {
    icon: BarChart3,
    title: 'Custom Reports',
    description:
      'Build reports that matter to your organization. Export data for board presentations and grant applications.',
  },
]

const dashboardMetrics = [
  { label: 'Website Visitors', value: '2,847', change: '+12%', trend: 'up' },
  { label: 'Email Open Rate', value: '42%', change: '+5%', trend: 'up' },
  { label: 'Event RSVPs', value: '156', change: '+23%', trend: 'up' },
  { label: 'Donations', value: '$3,240', change: '+8%', trend: 'up' },
]

const otherFeatures = [
  { title: 'Website', href: '/platform/features/website', description: 'Build your site' },
  { title: 'Events', href: '/platform/features/events', description: 'Manage concerts' },
  { title: 'Email', href: '/platform/features/email', description: 'Send newsletters' },
  { title: 'Donations', href: '/platform/features/donations', description: 'Accept support' },
]

export default function AnalyticsFeaturePage() {
  useEffect(() => {
    document.title = 'Analytics for Music Organizations | Fugue Lab Platform'
  }, [])

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const dashboardRef = useStaggeredGrid<HTMLDivElement>('.dashboard-card', { stagger: 0.1 })
  const featuresRef = useStaggeredGrid<HTMLDivElement>('.feature-card', { stagger: 0.1 })
  const useCasesRef = useScrollReveal<HTMLDivElement>('.use-case-card', { stagger: 0.1 })
  const pricingRef = useStaggeredGrid<HTMLDivElement>('.pricing-card', { stagger: 0.15 })
  const otherFeaturesRef = useStaggeredGrid<HTMLDivElement>('.other-feature-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="px-4 py-20">
        <div ref={heroRef} className="container mx-auto max-w-4xl text-center">
          <div className="bg-primary/10 text-primary mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </div>
          <h1 className="mb-6 text-4xl font-bold md:text-5xl">
            Know what is working
            <br />
            and what is not
          </h1>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            Track your website, email, events, and audience growth in one place. Make data-driven decisions to grow your
            organization.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <a href={`${APP_URL}/auth`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Start Tracking Growth <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </a>
            <Link to="/platform">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 text-lg">
                  See All Features
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground mt-4 text-sm">Included with every Fugue Lab website.</p>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="bg-muted/30 px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Your Organization at a Glance</h2>
            <p className="text-muted-foreground">One dashboard with everything you need to know.</p>
          </div>
          <div ref={dashboardRef} className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {dashboardMetrics.map((metric) => (
              <motion.div
                key={metric.label}
                className="dashboard-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <p className="text-muted-foreground mb-1 text-sm">{metric.label}</p>
                <p className="mb-1 text-3xl font-bold">{metric.value}</p>
                <p className={`text-sm ${metric.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                  {metric.change} this month
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Track Everything That Matters</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              Comprehensive analytics across your entire digital presence.
            </p>
          </div>
          <div ref={featuresRef} className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                className="feature-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <feature.icon className="text-primary mb-4 h-10 w-10" />
                <h3 className="mb-2 text-lg font-semibold">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-muted/30 px-4 py-20">
        <div ref={useCasesRef} className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Make Better Decisions</h2>
            <p className="text-muted-foreground">Analytics help you understand your audience and grow strategically.</p>
          </div>
          <div className="space-y-8">
            {[
              {
                question: 'Which concerts should we repeat?',
                answer:
                  'See which events had the highest attendance and engagement. Plan next season based on what your audience actually wants.',
              },
              {
                question: 'When should we send emails?',
                answer:
                  'Track open rates by day and time. Find the perfect moment when your audience is most likely to engage.',
              },
              {
                question: 'Where do our supporters come from?',
                answer:
                  'See traffic sources and referrers. Know whether social media, email, or search brings your best audience.',
              },
              {
                question: 'How healthy is our organization?',
                answer:
                  'Track audience growth, donation trends, and engagement over time. Export reports for board meetings and grants.',
              },
            ].map((item) => (
              <motion.div
                key={item.question}
                className="use-case-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <h3 className="mb-2 text-lg font-semibold">{item.question}</h3>
                <p className="text-muted-foreground">{item.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Analytics Integration */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="mb-6 text-3xl font-bold">Works with Google Analytics</h2>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            Already using Google Analytics? Connect your GA4 property and get the best of both worlds. Fugue Lab
            analytics for platform-specific insights, Google Analytics for deep web analytics.
          </p>
          <div className="border-border bg-card inline-flex items-center gap-4 rounded-lg border p-4">
            <div className="bg-muted flex h-10 w-10 items-center justify-center rounded">
              <BarChart3 className="text-muted-foreground h-6 w-6" />
            </div>
            <div className="text-left">
              <p className="font-semibold">Google Analytics 4</p>
              <p className="text-muted-foreground text-sm">Easy integration in Settings</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-muted/30 px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Simple Pricing</h2>
          </div>
          <div ref={pricingRef} className="mx-auto max-w-md">
            <motion.div
              className="pricing-card border-primary/50 bg-primary/5 rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary mb-2 text-sm font-medium">No separate charge</div>
              <h3 className="mb-2 text-2xl font-bold">Included with {websiteCapability.name}</h3>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">${websiteCapability.priceMonthly}</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-muted-foreground mb-6">
                Analytics are part of every Fugue Lab website. There is no separate charge.
              </p>
              <ul className="mb-8 space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Website, email, and event metrics in one dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Audience growth over time</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Google Analytics integration</span>
                </li>
              </ul>
              <Link to="/packages" className="block">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button className="w-full cursor-pointer">See All Pricing</Button>
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Other Features */}
      <section className="px-4 py-16">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold">More Features</h2>
            <p className="text-muted-foreground">Analytics work best with other Fugue Lab features.</p>
          </div>
          <div ref={otherFeaturesRef} className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {otherFeatures.map((feature) => (
              <motion.div
                key={feature.href}
                className="other-feature-card"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Link
                  to={feature.href}
                  className="border-border bg-card hover:border-primary block h-full rounded-lg border p-4 text-center transition-colors"
                >
                  <h3 className="font-semibold">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary/5 px-4 py-16">
        <div ref={ctaRef} className="container mx-auto max-w-3xl text-center">
          <div className="cta-content">
            <h2 className="mb-4 text-3xl font-bold">Ready to understand your audience?</h2>
            <p className="text-muted-foreground mb-8 text-xl">
              Join music organizations using Fugue Lab to make data-driven decisions.
            </p>
            <a href={`${APP_URL}/auth`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="inline-block"
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Start Tracking Growth <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
